import * as i0 from '@angular/core';
import { Component, ChangeDetectionStrategy, Inject, ElementRef, Directive, ContentChild, NgModule } from '@angular/core';
import * as i2 from '@angular/material/core';
import { MAT_DATE_FORMATS, DateAdapter } from '@angular/material/core';
import * as i1 from '@angular/material/datepicker';
import { yearsPerPage, MatDatepicker, MatDatepickerInput } from '@angular/material/datepicker';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import * as i3 from '@angular/material/button';
import { MatButtonModule } from '@angular/material/button';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInput, MatInputModule } from '@angular/material/input';
import { XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY, XPO_DATE_ADAPTER_MONTH_FORMATS_PROVIDER_FACTORY, XPO_DATE_ADAPTER_YEAR_FORMATS_PROVIDER_FACTORY } from '@xpo-ltl/ngx-ltl-core/utils';

var DatePickerViewTypeEnum;
(function (DatePickerViewTypeEnum) {
    DatePickerViewTypeEnum["Month"] = "month";
    DatePickerViewTypeEnum["Year"] = "year";
    DatePickerViewTypeEnum["MultiYear"] = "multi-year";
})(DatePickerViewTypeEnum || (DatePickerViewTypeEnum = {}));

const _c0 = ["*"];
class XpoDatePickerCustomHeaderComponent {
    constructor(_intl, _calendar, _dateAdapter, _dateFormats, changeDetectorRef) {
        this._intl = _intl;
        this._calendar = _calendar;
        this._dateAdapter = _dateAdapter;
        this._dateFormats = _dateFormats;
        this._destroyed = new Subject();
        this._calendar.stateChanges.pipe(takeUntil(this._destroyed)).subscribe(() => changeDetectorRef.markForCheck());
    }
    ngOnDestroy() {
        this._destroyed.next();
        this._destroyed.complete();
    }
    get periodButtonText() {
        if (this._calendar.startView === DatePickerViewTypeEnum.Month) {
            if (this._calendar.currentView === DatePickerViewTypeEnum.Month) {
                return this._dateAdapter
                    .format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel)
                    .toLocaleUpperCase();
            }
            return this._dateAdapter.getYearName(this._calendar.activeDate);
        }
        else {
            if (this._calendar.currentView === DatePickerViewTypeEnum.Month) {
                return this._dateAdapter
                    .format(this._calendar.activeDate, this._dateFormats.display.monthYearLabel)
                    .toLocaleUpperCase();
            }
            if (this._calendar.currentView === DatePickerViewTypeEnum.Year) {
                return this._dateAdapter.getYearName(this._calendar.activeDate);
            }
            return this.yearsRangeLabel();
        }
    }
    yearsRangeLabel() {
        const activeYear = this._dateAdapter.getYear(this._calendar.activeDate);
        const minYearOfPage = activeYear -
            this._getActiveOffset(this._dateAdapter, this._calendar.activeDate, this._calendar.minDate, this._calendar.maxDate);
        const maxYearOfPage = minYearOfPage + yearsPerPage - 1;
        const minYearName = this._dateAdapter.getYearName(this._dateAdapter.createDate(minYearOfPage, 0, 1));
        const maxYearName = this._dateAdapter.getYearName(this._dateAdapter.createDate(maxYearOfPage, 0, 1));
        return this.formatYearRange(minYearName, maxYearName);
    }
    formatYearRange(start, end) {
        return `${start} \u2013 ${end}`;
    }
    get periodButtonLabel() {
        return this._calendar.currentView === DatePickerViewTypeEnum.Month
            ? this._intl.switchToMultiYearViewLabel
            : this._intl.switchToMonthViewLabel;
    }
    get prevButtonLabel() {
        let label = '';
        switch (this._calendar.currentView) {
            case DatePickerViewTypeEnum.Month:
                label = this._intl.prevMonthLabel;
                break;
            case DatePickerViewTypeEnum.Year:
                label = this._intl.prevYearLabel;
                break;
            case DatePickerViewTypeEnum.MultiYear:
                label = this._intl.prevMultiYearLabel;
                break;
            default:
                break;
        }
        return label;
    }
    get nextButtonLabel() {
        let label = '';
        switch (this._calendar.currentView) {
            case DatePickerViewTypeEnum.Month:
                label = this._intl.nextMonthLabel;
                break;
            case DatePickerViewTypeEnum.Year:
                label = this._intl.nextYearLabel;
                break;
            case DatePickerViewTypeEnum.MultiYear:
                label = this._intl.nextMultiYearLabel;
                break;
            default:
                break;
        }
        return label;
    }
    currentPeriodClicked() {
        if (this._calendar.startView === DatePickerViewTypeEnum.Year) {
            this._calendar.currentView =
                this._calendar.currentView === DatePickerViewTypeEnum.Year
                    ? DatePickerViewTypeEnum.MultiYear
                    : DatePickerViewTypeEnum.Year;
        }
        else if (this._calendar.startView !== DatePickerViewTypeEnum.MultiYear) {
            if (this._calendar.currentView === DatePickerViewTypeEnum.Month) {
                this._calendar.currentView = DatePickerViewTypeEnum.Year;
            }
            else if (this._calendar.currentView === DatePickerViewTypeEnum.Year) {
                this._calendar.currentView = DatePickerViewTypeEnum.MultiYear;
            }
            else if (this._calendar.currentView === DatePickerViewTypeEnum.MultiYear) {
                this._calendar.currentView = DatePickerViewTypeEnum.Month;
            }
        }
    }
    previousClicked() {
        this._calendar.activeDate =
            this._calendar.currentView === DatePickerViewTypeEnum.Month
                ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, -1)
                : this._dateAdapter.addCalendarYears(this._calendar.activeDate, this._calendar.currentView === DatePickerViewTypeEnum.Year ? -1 : -yearsPerPage);
    }
    nextClicked() {
        this._calendar.activeDate =
            this._calendar.currentView === DatePickerViewTypeEnum.Month
                ? this._dateAdapter.addCalendarMonths(this._calendar.activeDate, 1)
                : this._dateAdapter.addCalendarYears(this._calendar.activeDate, this._calendar.currentView === DatePickerViewTypeEnum.Year ? 1 : yearsPerPage);
    }
    previousEnabled() {
        if (!this._calendar.minDate) {
            return true;
        }
        return !this._calendar.minDate || !this._isSameView(this._calendar.activeDate, this._calendar.minDate);
    }
    nextEnabled() {
        return !this._calendar.maxDate || !this._isSameView(this._calendar.activeDate, this._calendar.maxDate);
    }
    _isSameView(date1, date2) {
        if (this._calendar.currentView === DatePickerViewTypeEnum.Month) {
            return (this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2) &&
                this._dateAdapter.getMonth(date1) === this._dateAdapter.getMonth(date2));
        }
        if (this._calendar.currentView === DatePickerViewTypeEnum.Year) {
            return this._dateAdapter.getYear(date1) === this._dateAdapter.getYear(date2);
        }
        return this._isSameMultiYearView(this._dateAdapter, date1, date2, this._calendar.minDate, this._calendar.maxDate);
    }
    _isSameMultiYearView(dateAdapter, date1, date2, minDate, maxDate) {
        const year1 = dateAdapter.getYear(date1);
        const year2 = dateAdapter.getYear(date2);
        const startingYear = this._getStartingYear(dateAdapter, minDate, maxDate);
        return Math.floor((year1 - startingYear) / yearsPerPage) === Math.floor((year2 - startingYear) / yearsPerPage);
    }
    _getActiveOffset(dateAdapter, activeDate, minDate, maxDate) {
        const activeYear = dateAdapter.getYear(activeDate);
        return this._euclideanModulo(activeYear - this._getStartingYear(dateAdapter, minDate, maxDate), yearsPerPage);
    }
    _getStartingYear(dateAdapter, minDate, maxDate) {
        let startingYear = 0;
        if (maxDate) {
            const maxYear = dateAdapter.getYear(maxDate);
            startingYear = maxYear - yearsPerPage + 1;
        }
        else if (minDate) {
            startingYear = dateAdapter.getYear(minDate);
        }
        return startingYear;
    }
    _euclideanModulo(a, b) {
        return ((a % b) + b) % b;
    }
}
XpoDatePickerCustomHeaderComponent.ɵfac = function XpoDatePickerCustomHeaderComponent_Factory(t) { return new (t || XpoDatePickerCustomHeaderComponent)(i0.ɵɵdirectiveInject(i1.MatDatepickerIntl), i0.ɵɵdirectiveInject(i1.MatCalendar), i0.ɵɵdirectiveInject(i2.DateAdapter), i0.ɵɵdirectiveInject(MAT_DATE_FORMATS), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef)); };
XpoDatePickerCustomHeaderComponent.ɵcmp = /*@__PURE__*/ i0.ɵɵdefineComponent({ type: XpoDatePickerCustomHeaderComponent, selectors: [["xpo-datepicker-custom-header"]], ngContentSelectors: _c0, decls: 7, vars: 6, consts: [[1, "mat-calendar-header"], [1, "mat-calendar-controls"], ["type", "button", "mat-icon-button", "", 1, "mat-calendar-previous-button", 3, "disabled", "click"], ["type", "button", "mat-button", "", "cdkAriaLive", "polite", 1, "mat-calendar-period-button", 3, "click"], ["type", "button", "mat-icon-button", "", 1, "mat-calendar-next-button", 3, "disabled", "click"]], template: function XpoDatePickerCustomHeaderComponent_Template(rf, ctx) { if (rf & 1) {
        i0.ɵɵprojectionDef();
        i0.ɵɵelementStart(0, "div", 0);
        i0.ɵɵelementStart(1, "div", 1);
        i0.ɵɵelementStart(2, "button", 2);
        i0.ɵɵlistener("click", function XpoDatePickerCustomHeaderComponent_Template_button_click_2_listener() { return ctx.previousClicked(); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementStart(3, "button", 3);
        i0.ɵɵlistener("click", function XpoDatePickerCustomHeaderComponent_Template_button_click_3_listener() { return ctx.currentPeriodClicked(); });
        i0.ɵɵtext(4);
        i0.ɵɵelementEnd();
        i0.ɵɵprojection(5);
        i0.ɵɵelementStart(6, "button", 4);
        i0.ɵɵlistener("click", function XpoDatePickerCustomHeaderComponent_Template_button_click_6_listener() { return ctx.nextClicked(); });
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
        i0.ɵɵelementEnd();
    } if (rf & 2) {
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("disabled", !ctx.previousEnabled());
        i0.ɵɵattribute("aria-label", ctx.prevButtonLabel);
        i0.ɵɵadvance(1);
        i0.ɵɵattribute("aria-label", ctx.periodButtonLabel);
        i0.ɵɵadvance(1);
        i0.ɵɵtextInterpolate1(" ", ctx.periodButtonText, " ");
        i0.ɵɵadvance(2);
        i0.ɵɵproperty("disabled", !ctx.nextEnabled());
        i0.ɵɵattribute("aria-label", ctx.nextButtonLabel);
    } }, directives: [i3.MatButton], styles: [""], changeDetection: 0 });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDatePickerCustomHeaderComponent, [{
        type: Component,
        args: [{
                selector: 'xpo-datepicker-custom-header',
                styleUrls: ['datepicker-custom-header.component.scss'],
                templateUrl: 'datepicker-custom-header.component.html',
                changeDetection: ChangeDetectionStrategy.OnPush,
            }]
    }], function () { return [{ type: i1.MatDatepickerIntl }, { type: i1.MatCalendar }, { type: i2.DateAdapter }, { type: undefined, decorators: [{
                type: Inject,
                args: [MAT_DATE_FORMATS]
            }] }, { type: i0.ChangeDetectorRef }]; }, null); })();

const XPO_DATEPICKER_CALENDAR_CLASS = 'xpo-DatePicker-calendar';
const XPO_DATEPICKER_ACTIVE_CLASS = 'xpo-DatePicker--active';
class XpoBaseDatePickerDirective {
    constructor(element, renderer, dateAdapter) {
        this.element = element;
        this.renderer = renderer;
        this.dateAdapter = dateAdapter;
    }
    ngAfterContentInit() {
        this.datePicker.panelClass = XPO_DATEPICKER_CALENDAR_CLASS;
        this.datePicker.calendarHeaderComponent = XpoDatePickerCustomHeaderComponent;
        this.datePicker.openedStream.subscribe(() => this.onCalendarOpened());
        this.datePicker.closedStream.subscribe(() => this.onCalendarClosed());
        this.renderer.setAttribute(this.datePickerInputElement.nativeElement, 'autocomplete', 'off');
        this.datePickerMatInput.onContainerClick = () => {
            this.onClicked();
        };
    }
    onCalendarOpened() {
        this.renderer.addClass(this.element.nativeElement, XPO_DATEPICKER_ACTIVE_CLASS);
    }
    onCalendarClosed() {
        this.renderer.removeClass(this.element.nativeElement, XPO_DATEPICKER_ACTIVE_CLASS);
    }
    onClicked() {
        if (this.datePicker.opened) {
            return;
        }
        this.datePicker.open();
    }
}
XpoBaseDatePickerDirective.ɵfac = function XpoBaseDatePickerDirective_Factory(t) { return new (t || XpoBaseDatePickerDirective)(i0.ɵɵdirectiveInject(i0.ElementRef), i0.ɵɵdirectiveInject(i0.Renderer2), i0.ɵɵdirectiveInject(i2.DateAdapter)); };
XpoBaseDatePickerDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoBaseDatePickerDirective, contentQueries: function XpoBaseDatePickerDirective_ContentQueries(rf, ctx, dirIndex) { if (rf & 1) {
        i0.ɵɵcontentQuery(dirIndex, MatDatepicker, 7);
        i0.ɵɵcontentQuery(dirIndex, MatInput, 7, ElementRef);
        i0.ɵɵcontentQuery(dirIndex, MatInput, 7);
        i0.ɵɵcontentQuery(dirIndex, MatDatepickerInput, 7);
    } if (rf & 2) {
        let _t;
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.datePicker = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.datePickerInputElement = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.datePickerMatInput = _t.first);
        i0.ɵɵqueryRefresh(_t = i0.ɵɵloadQuery()) && (ctx.datePickerInput = _t.first);
    } } });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoBaseDatePickerDirective, [{
        type: Directive
    }], function () { return [{ type: i0.ElementRef }, { type: i0.Renderer2 }, { type: i2.DateAdapter }]; }, { datePicker: [{
            type: ContentChild,
            args: [MatDatepicker, { static: true }]
        }], datePickerInputElement: [{
            type: ContentChild,
            args: [MatInput, { static: true, read: ElementRef }]
        }], datePickerMatInput: [{
            type: ContentChild,
            args: [MatInput, { static: true }]
        }], datePickerInput: [{
            type: ContentChild,
            args: [MatDatepickerInput, { static: true }]
        }] }); })();

const XPO_DATEPICKER_PLACEHOLDER = 'MM/DD/YYYY';
class XpoDatePickerDirective extends XpoBaseDatePickerDirective {
    ngAfterContentInit() {
        super.ngAfterContentInit();
        this.datePickerMatInput.placeholder = XPO_DATEPICKER_PLACEHOLDER;
    }
}
XpoDatePickerDirective.ɵfac = /*@__PURE__*/ function () { let ɵXpoDatePickerDirective_BaseFactory; return function XpoDatePickerDirective_Factory(t) { return (ɵXpoDatePickerDirective_BaseFactory || (ɵXpoDatePickerDirective_BaseFactory = i0.ɵɵgetInheritedFactory(XpoDatePickerDirective)))(t || XpoDatePickerDirective); }; }();
XpoDatePickerDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoDatePickerDirective, selectors: [["", "xpoDatePicker", ""]], hostAttrs: [1, "xpo-DatePicker"], features: [i0.ɵɵProvidersFeature([
            { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
        ]), i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDatePickerDirective, [{
        type: Directive,
        args: [{
                selector: '[xpoDatePicker]',
                host: {
                    class: 'xpo-DatePicker',
                },
                providers: [
                    { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_DATE_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
                ],
            }]
    }], null, null); })();

const XPO_MONTH_PICKER_PLACEHOLDER = 'MM/YYYY';
class XpoMonthPickerViewDirective extends XpoBaseDatePickerDirective {
    ngAfterContentInit() {
        super.ngAfterContentInit();
        this.datePickerMatInput.placeholder = XPO_MONTH_PICKER_PLACEHOLDER;
        this.datePicker.startView = 'year';
        this.datePicker.monthSelected.subscribe((selectedMonth) => this.onMonthSelected(selectedMonth));
    }
    onMonthSelected(selectedMonth) {
        const monthValue = this.dateAdapter.createDate(this.dateAdapter.getYear(selectedMonth), this.dateAdapter.getMonth(selectedMonth), 1);
        this.datePicker.select(monthValue);
        this.datePicker.close();
    }
}
XpoMonthPickerViewDirective.ɵfac = /*@__PURE__*/ function () { let ɵXpoMonthPickerViewDirective_BaseFactory; return function XpoMonthPickerViewDirective_Factory(t) { return (ɵXpoMonthPickerViewDirective_BaseFactory || (ɵXpoMonthPickerViewDirective_BaseFactory = i0.ɵɵgetInheritedFactory(XpoMonthPickerViewDirective)))(t || XpoMonthPickerViewDirective); }; }();
XpoMonthPickerViewDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoMonthPickerViewDirective, selectors: [["", "xpoMonthPickerView", ""]], hostAttrs: [1, "xpo-DatePicker"], features: [i0.ɵɵProvidersFeature([
            { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_MONTH_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
        ]), i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoMonthPickerViewDirective, [{
        type: Directive,
        args: [{
                selector: '[xpoMonthPickerView]',
                host: {
                    class: 'xpo-DatePicker',
                },
                providers: [
                    { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_MONTH_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
                ],
            }]
    }], null, null); })();

const XPO_YEAR_PICKER_PLACEHOLDER = 'YYYY';
class XpoYearPickerDirective extends XpoBaseDatePickerDirective {
    ngAfterContentInit() {
        super.ngAfterContentInit();
        this.datePickerMatInput.placeholder = XPO_YEAR_PICKER_PLACEHOLDER;
        this.datePicker.startView = 'multi-year';
        this.datePicker.yearSelected.subscribe((selectedYear) => this.onYearSelected(selectedYear));
    }
    onYearSelected(selectedYear) {
        const yearValue = this.dateAdapter.createDate(this.dateAdapter.getYear(selectedYear), 0, 1);
        this.datePicker.select(yearValue);
        this.datePicker.close();
    }
}
XpoYearPickerDirective.ɵfac = /*@__PURE__*/ function () { let ɵXpoYearPickerDirective_BaseFactory; return function XpoYearPickerDirective_Factory(t) { return (ɵXpoYearPickerDirective_BaseFactory || (ɵXpoYearPickerDirective_BaseFactory = i0.ɵɵgetInheritedFactory(XpoYearPickerDirective)))(t || XpoYearPickerDirective); }; }();
XpoYearPickerDirective.ɵdir = /*@__PURE__*/ i0.ɵɵdefineDirective({ type: XpoYearPickerDirective, selectors: [["", "xpoYearPicker", ""]], hostAttrs: [1, "xpo-DatePicker"], features: [i0.ɵɵProvidersFeature([
            { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_YEAR_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
        ]), i0.ɵɵInheritDefinitionFeature] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoYearPickerDirective, [{
        type: Directive,
        args: [{
                selector: '[xpoYearPicker]',
                host: {
                    class: 'xpo-DatePicker',
                },
                providers: [
                    { provide: MAT_DATE_FORMATS, useFactory: XPO_DATE_ADAPTER_YEAR_FORMATS_PROVIDER_FACTORY, deps: [DateAdapter] },
                ],
            }]
    }], null, null); })();

class XpoDatePickerModule {
}
XpoDatePickerModule.ɵfac = function XpoDatePickerModule_Factory(t) { return new (t || XpoDatePickerModule)(); };
XpoDatePickerModule.ɵmod = /*@__PURE__*/ i0.ɵɵdefineNgModule({ type: XpoDatePickerModule });
XpoDatePickerModule.ɵinj = /*@__PURE__*/ i0.ɵɵdefineInjector({ imports: [[
            CommonModule,
            FormsModule,
            MatButtonModule,
            MatIconModule,
            MatInputModule,
            MatFormFieldModule,
            ReactiveFormsModule
        ]] });
(function () { (typeof ngDevMode === "undefined" || ngDevMode) && i0.ɵsetClassMetadata(XpoDatePickerModule, [{
        type: NgModule,
        args: [{
                imports: [
                    CommonModule,
                    FormsModule,
                    MatButtonModule,
                    MatIconModule,
                    MatInputModule,
                    MatFormFieldModule,
                    ReactiveFormsModule
                ],
                entryComponents: [XpoDatePickerCustomHeaderComponent],
                exports: [XpoDatePickerDirective, XpoMonthPickerViewDirective, XpoYearPickerDirective],
                declarations: [
                    XpoDatePickerCustomHeaderComponent,
                    XpoDatePickerDirective,
                    XpoMonthPickerViewDirective,
                    XpoYearPickerDirective,
                ],
            }]
    }], null, null); })();
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && i0.ɵɵsetNgModuleScope(XpoDatePickerModule, { declarations: [XpoDatePickerCustomHeaderComponent,
        XpoDatePickerDirective,
        XpoMonthPickerViewDirective,
        XpoYearPickerDirective], imports: [CommonModule,
        FormsModule,
        MatButtonModule,
        MatIconModule,
        MatInputModule,
        MatFormFieldModule,
        ReactiveFormsModule], exports: [XpoDatePickerDirective, XpoMonthPickerViewDirective, XpoYearPickerDirective] }); })();

/**
 * Generated bundle index. Do not edit.
 */

export { XpoDatePickerCustomHeaderComponent, XpoDatePickerDirective, XpoDatePickerModule, XpoMonthPickerViewDirective, XpoYearPickerDirective };
